import {getValueByDevice, useDevice} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {CHART_GRID_COLUMNS} from '../constants/CHART_GRID_COLUMNS';
import type {ChartCardWidth} from '../types/ChartCardWidth';
import type {ChartLayout} from '../types/ChartLayout';
import {readStoredChartLayout} from '../utils/readStoredChartLayout';
import {writeStoredChartLayout} from '../utils/writeStoredChartLayout';

interface ChartLayoutDraft {
  /** The chart grid that edit mode started on. */
  layoutId: string;
  /** The half cards of that chart grid only. */
  widths: ChartLayout;
}

const isInGrid = (cardId: string, layoutId: string) => cardId.startsWith(`${layoutId}.`);

const filterCards = (layout: ChartLayout, isKept: (cardId: string) => boolean): ChartLayout =>
  Object.fromEntries(Object.entries(layout).filter(([cardId]) => isKept(cardId)));

interface UseChartLayoutOptions {
  /** Layout id of the chart grid on the visible tab, or `undefined` for a tab without one. */
  layoutId: string | undefined;
}

/**
 * The layout of the chart cards on a reporting page and its edit mode, "Customise dashboard".
 * Edits change a draft of the visible chart grid, and only Save stores it, as on the DMS home
 * page. A window without the two-column grid only hides edit mode, and the draft survives. The draft also
 * stays with the tab that edit mode started on when the browser history changes the tab.
 */
export function useChartLayout(options: UseChartLayoutOptions) {
  const device = useDevice();
  const [savedLayout, setSavedLayout] = useState(readStoredChartLayout);
  const [draft, setDraft] = useState<ChartLayoutDraft | null>(null);

  // Half width shows only where the chart grid has two columns, so edit mode is offered only there.
  const hasTwoColumns = getValueByDevice(device, ...CHART_GRID_COLUMNS) > 1;
  const isAvailable = hasTwoColumns && isNotNil(options.layoutId);
  const editedDraft =
    isAvailable && isNotNil(draft) && draft.layoutId === options.layoutId ? draft : null;

  const updateDraftWidths = (update: (widths: ChartLayout) => ChartLayout) =>
    setDraft((current) =>
      isNil(current) ? current : {...current, widths: update(current.widths)}
    );

  const startEditing = () => {
    const layoutId = options.layoutId;
    if (!isAvailable || isNil(layoutId)) {
      return;
    }
    setDraft({layoutId, widths: filterCards(savedLayout, (cardId) => isInGrid(cardId, layoutId))});
  };

  // Rereads the stored layout and replaces only the edited chart grid, so a Save does not drop the
  // widths that another browser tab stored meanwhile for the other grids.
  const saveLayout = () => {
    if (isNil(editedDraft)) {
      return;
    }
    const nextLayout = {
      ...filterCards(readStoredChartLayout(), (cardId) => !isInGrid(cardId, editedDraft.layoutId)),
      ...editedDraft.widths,
    };
    writeStoredChartLayout(nextLayout);
    setSavedLayout(nextLayout);
    setDraft(null);
  };

  return {
    /** "Customise dashboard" is offered: the window shows the two-column grid and the visible tab
     * has a chart grid. */
    isAvailable,
    isEditing: isNotNil(editedDraft),
    getWidth: (cardId: string): ChartCardWidth =>
      (editedDraft?.widths ?? savedLayout)[cardId] ?? 'full',
    setWidth: (cardId: string, width: ChartCardWidth) =>
      updateDraftWidths((widths) =>
        width === 'half'
          ? {...widths, [cardId]: 'half'}
          : filterCards(widths, (draftCardId) => draftCardId !== cardId)
      ),
    startEditing,
    cancelEditing: () => setDraft(null),
    // Sets every card of the edited chart grid back to full width. Save stores it.
    resetLayout: () => updateDraftWidths(() => ({})),
    saveLayout,
  };
}

export type ChartLayoutState = ReturnType<typeof useChartLayout>;
